import {useCallback, useEffect, useRef, useState} from 'react';
import {RichBlockComponentProps} from './blockComponents.tsx';
import {ImageBlockData} from '../../../types/api.ts';
import ImageBlock from './ImageBlock.tsx';

export default function ImagesBlock({
    data: {images},
    ...rest
}: RichBlockComponentProps<ImageBlockData>) {
    const trackRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const handleScroll = useCallback(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        const index = Math.round(track.scrollLeft / track.clientWidth);
        setActiveIndex(index);
    }, []);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        track.addEventListener('scroll', handleScroll, {passive: true});
        return () => track.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const goTo = useCallback((index: number) => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        track.scrollTo({
            left: index * track.clientWidth,
            behavior: 'smooth',
        });
    }, []);

    if (!images || !images.length) {
        return null;
    }

    if (images.length === 1) {
        return (
            <div>
                <ImageBlock {...rest} data={images[0]} />
            </div>
        );
    }

    return (
        <div>
            <div
                ref={trackRef}
                style={{
                    display: 'flex',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {images.map((image, index) => (
                    <div
                        key={index}
                        style={{
                            flex: '0 0 100%',
                            width: '100%',
                            scrollSnapAlign: 'start',
                        }}
                    >
                        <ImageBlock {...rest} data={image} />
                    </div>
                ))}
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    marginTop: '12px',
                }}
            >
                {images.map((_, index) => (
                    <button
                        key={index}
                        type={'button'}
                        aria-label={`Go to image ${index + 1}`}
                        onClick={() => goTo(index)}
                        style={{
                            width: '8px',
                            height: '8px',
                            padding: 0,
                            border: 'none',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            backgroundColor:
                                index === activeIndex
                                    ? 'rgba(0, 0, 0, 0.7)'
                                    : 'rgba(0, 0, 0, 0.25)',
                            transition: 'background-color 0.2s ease',
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
